// src/types/audit.ts
// TypeScript types for the Audit System

export type AuditEventType =
  | 'SERIALIZED_ITEM_CREATED'
  | 'SERIALIZED_ITEM_UPDATED'
  | 'SERIALIZED_ITEM_DELETED'
  | 'SERIALIZED_ITEM_ASSIGNED'
  | 'SERIALIZED_ITEM_RETURNED'
  | 'SERIALIZED_ITEM_TRANSFERRED'
  | 'SERIALIZED_ITEM_CONDITION_CHANGED'
  | 'SERIALIZED_ITEM_RETIRED'
  | 'SERIALIZED_ITEM_DESTROYED'
  | 'SERIALIZED_ITEM_SERIAL_CHANGED'
  | 'SERIALIZED_ASSIGNMENT_UPDATED';

export type AuditAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'ASSIGN' | 'RETURN' | 'TRANSFER';

export type AuditTargetType = 'SERIALIZED_ITEM' | 'SERIALIZED_ASSIGNMENT';

export interface AuditKeyChange {
  from: string | null;
  to: string | null;
}

export interface AuditKeyChanges {
  status?: AuditKeyChange;
  condition?: AuditKeyChange;
  serialNumber?: AuditKeyChange;
  assignedToUserId?: AuditKeyChange;
  assignedToName?: AuditKeyChange;
  location?: AuditKeyChange;
  [key: string]: AuditKeyChange | undefined;
}

export interface AuditRecord {
  // Keys
  tenantId: string;
  timestampAuditId: string;
  
  // Core fields
  timestamp: string;
  auditId: string;
  
  // WHO did it
  actorUserId: string;
  actorEmail: string;
  actorRole: string;
  
  // WHAT was affected
  targetType: AuditTargetType;
  targetId: string;
  targetName: string;
  targetSerial: string | null;
  
  // WHAT action
  action: AuditAction;
  eventType: AuditEventType;
  description: string;
  
  // WHAT changed
  changedFields?: string[];
  keyChanges?: AuditKeyChanges;
  
  // Metadata
  sourceFunction: string;
  sourceTable: string;
}

export interface AuditQueryFilters {
  startDate?: string;
  endDate?: string;
  eventType?: AuditEventType;
  limit?: number;
  lastEvaluatedKey?: string;
}

export interface AuditQueryResponse {
  success: boolean;
  data: AuditRecord[];
  count: number;
  lastEvaluatedKey: string | null;
  error?: {
    code: string;
    message: string;
  };
}

// Event type display configuration
export const AUDIT_EVENT_TYPES: { value: AuditEventType; label: string; color: string }[] = [
  { value: 'SERIALIZED_ITEM_CREATED', label: 'Item Created', color: 'bg-green-100 text-green-800' },
  { value: 'SERIALIZED_ITEM_UPDATED', label: 'Item Updated', color: 'bg-blue-100 text-blue-800' },
  { value: 'SERIALIZED_ITEM_DELETED', label: 'Item Deleted', color: 'bg-red-100 text-red-800' },
  { value: 'SERIALIZED_ITEM_ASSIGNED', label: 'Item Assigned', color: 'bg-purple-100 text-purple-800' },
  { value: 'SERIALIZED_ITEM_RETURNED', label: 'Item Returned', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'SERIALIZED_ITEM_TRANSFERRED', label: 'Item Transferred', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'SERIALIZED_ITEM_CONDITION_CHANGED', label: 'Condition Changed', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'SERIALIZED_ITEM_RETIRED', label: 'Item Retired', color: 'bg-gray-100 text-gray-800' },
  { value: 'SERIALIZED_ITEM_DESTROYED', label: 'Item Destroyed', color: 'bg-red-100 text-red-800' },
  { value: 'SERIALIZED_ITEM_SERIAL_CHANGED', label: 'Serial Changed', color: 'bg-orange-100 text-orange-800' },
  { value: 'SERIALIZED_ASSIGNMENT_UPDATED', label: 'Assignment Updated', color: 'bg-teal-100 text-teal-800' },
];

// Helper to get event type display info
export function getEventTypeDisplay(eventType: AuditEventType): { label: string; color: string } {
  const found = AUDIT_EVENT_TYPES.find(e => e.value === eventType);
  return found || { label: eventType, color: 'bg-gray-100 text-gray-800' };
}

// Helper to get action display color
export function getActionColor(action: AuditAction): string {
  switch (action) {
    case 'CREATE':
      return 'text-green-600';
    case 'UPDATE':
      return 'text-blue-600';
    case 'DELETE':
      return 'text-red-600';
    case 'ASSIGN':
      return 'text-purple-600';
    case 'RETURN':
      return 'text-indigo-600';
    case 'TRANSFER':
      return 'text-cyan-600';
    default:
      return 'text-gray-600';
  }
}
